import vtkAngle2DWidgetFactory from "./Angle2DWidget";
import { bindSVGRepresentation } from '../js/SVGHelpers';
import { useEffect, useRef } from "react";
import vtkInteractorObserver from 'vtk.js/Sources/Rendering/Core/InteractorObserver';
import { logDebug, debugCallback, isNullOrUndefined, WidgetType, getDUName, SlicingModeName, AngleMeasurementStateName } from '../js/Common';
const { computeWorldToDisplay } = vtkInteractorObserver;

const isDebugging = false;

function log(msg, ...params) {
    logDebug("DicomVisualization.Angle", isDebugging, msg, ...params);
}

function debug(callback) {
    debugCallback(isDebugging, callback);
}

function isValidPoint(point) {
    return Array.isArray(point) && point.length === 2 && !point.some(Number.isNaN);
}

function calculatePerpendicularHandleCoordinates(center, lineStart, lineEnd) {
    if (!isValidPoint(center) || !isValidPoint(lineStart) || !isValidPoint(lineEnd)) {
        return null;
    }
    const tickLength = 16;
    const halfTickLength = tickLength / 2;
    const dx = lineEnd[0] - lineStart[0];
    const dy = lineEnd[1] - lineStart[1];
    const length = Math.hypot(dx, dy);

    if (length === 0) {
        return {
            x1: center[0],
            y1: center[1] - halfTickLength,
            x2: center[0],
            y2: center[1] + halfTickLength
        };
    }

    const px = -dy / length;
    const py = dx / length;

    return {
        x1: center[0] - px * halfTickLength,
        y1: center[1] - py * halfTickLength,
        x2: center[0] + px * halfTickLength,
        y2: center[1] + py * halfTickLength
    };
}

function computeArcRadius(len1, len2) {
    return Math.max(8, Math.min(60, Math.min(len1, len2) * 0.35));
}

function buildAngleArcPath(vertexPos, arm1Pos, arm2Pos) {
    if (!isValidPoint(vertexPos) || !isValidPoint(arm1Pos) || !isValidPoint(arm2Pos)) {
        return null;
    }
    const dx1 = arm1Pos[0] - vertexPos[0];
    const dy1 = arm1Pos[1] - vertexPos[1];
    const dx2 = arm2Pos[0] - vertexPos[0];
    const dy2 = arm2Pos[1] - vertexPos[1];
    const len1 = Math.hypot(dx1, dy1);
    const len2 = Math.hypot(dx2, dy2);

    if (len1 === 0 || len2 === 0) return null;

    const arcRadius = computeArcRadius(len1, len2);

    const arcStartX = vertexPos[0] + (dx1 / len1) * arcRadius;
    const arcStartY = vertexPos[1] + (dy1 / len1) * arcRadius;
    const arcEndX = vertexPos[0] + (dx2 / len2) * arcRadius;
    const arcEndY = vertexPos[1] + (dy2 / len2) * arcRadius;

    if (Math.abs(arcStartX - arcEndX) < 0.5 && Math.abs(arcStartY - arcEndY) < 0.5) {
        return null;
    }

    const cross = dx1 * dy2 - dy1 * dx2;
    const sweepFlag = cross > 0 ? 1 : 0;

    return `M ${arcStartX} ${arcStartY} A ${arcRadius} ${arcRadius} 0 0 ${sweepFlag} ${arcEndX} ${arcEndY}`;
}

function setupSVG(w, renderer, genericRenderWindow) {
    return (
        bindSVGRepresentation(renderer, w.getWidgetState(), genericRenderWindow, {
            mapState(widgetState, { renderWindowSize }) {
                if (widgetState.isDeleted() || !w.getPickable() || isNullOrUndefined(renderer) || renderer.isDeleted()) { return null; }
                const textState = widgetState.getText();
                const text = textState.getText();
                const vertexOrigin = widgetState.getHandle2().getOrigin();
                const arm1Origin = widgetState.getHandle1().getOrigin();
                const arm2Origin = widgetState.getHandle3()?.getOrigin();
                const moveHandleOrigin = widgetState.getMoveHandle()?.getOrigin();

                debug(() => global.debugAngleW = w);

                let moveHandlePosition = null;
                if (moveHandleOrigin) {
                    const moveCoords = computeWorldToDisplay(renderer, ...moveHandleOrigin);
                    moveHandlePosition = [moveCoords[0], renderWindowSize[1] - moveCoords[1]];
                }

                if (vertexOrigin && arm1Origin) {
                    const vertexCoords = computeWorldToDisplay(renderer, ...vertexOrigin);
                    const vertexPosition = [vertexCoords[0], renderWindowSize[1] - vertexCoords[1]];
                    const arm1Coords = computeWorldToDisplay(renderer, ...arm1Origin);
                    const arm1Position = [arm1Coords[0], renderWindowSize[1] - arm1Coords[1]];

                    let arm2Position = null;
                    if (arm2Origin) {
                        const arm2Coords = computeWorldToDisplay(renderer, ...arm2Origin);
                        arm2Position = [arm2Coords[0], renderWindowSize[1] - arm2Coords[1]];
                    }

                    return {
                        text,
                        vertexPosition,
                        arm1Position,
                        arm2Position,
                        moveHandlePosition,
                    };
                }
                return null;
            },
            render(data, h) {
                if (!data) { return []; }

                const nodes = [];
                const vertexValid = isValidPoint(data.vertexPosition);
                const arm1Valid = isValidPoint(data.arm1Position);
                const arm2Valid = isValidPoint(data.arm2Position);
                const moveValid = isValidPoint(data.moveHandlePosition);

                const liveArm2 = arm2Valid ? data.arm2Position : (moveValid ? data.moveHandlePosition : null);

                if (vertexValid && arm1Valid) {
                    nodes.push(
                        h('line', {
                            key: 'lineArm1',
                            attrs: {
                                x1: data.vertexPosition[0],
                                y1: data.vertexPosition[1],
                                x2: data.arm1Position[0],
                                y2: data.arm1Position[1],
                                stroke: 'red',
                                'stroke-width': 2
                            }
                        })
                    );
                }

                if (vertexValid && liveArm2) {
                    nodes.push(
                        h('line', {
                            key: 'lineArm2',
                            attrs: {
                                x1: data.vertexPosition[0],
                                y1: data.vertexPosition[1],
                                x2: liveArm2[0],
                                y2: liveArm2[1],
                                stroke: 'red',
                                'stroke-width': 2
                            }
                        })
                    );
                }

                if (vertexValid && arm1Valid && arm2Valid) {
                    const arcPath = buildAngleArcPath(data.vertexPosition, data.arm1Position, data.arm2Position);
                    if (arcPath) {
                        nodes.push(
                            h('path', {
                                key: 'angleArc',
                                attrs: {
                                    d: arcPath,
                                    stroke: 'yellow',
                                    'stroke-width': 2,
                                    fill: 'none'
                                }
                            })
                        );
                    }
                }

                const arm1Tick = arm1Valid
                    ? calculatePerpendicularHandleCoordinates(data.arm1Position, data.vertexPosition, data.arm1Position)
                    : null;
                if (arm1Tick) {
                    nodes.push(
                        h('line', {
                            key: 'arm1Tick',
                            attrs: {
                                ...arm1Tick,
                                stroke: 'yellow',
                                'stroke-width': 3
                            }
                        })
                    );
                }

                const tickTarget = arm2Valid ? data.arm2Position : (moveValid ? data.moveHandlePosition : null);
                if (tickTarget && vertexValid) {
                    const arm2Tick = calculatePerpendicularHandleCoordinates(tickTarget, data.vertexPosition, tickTarget);
                    if (arm2Tick) {
                        nodes.push(
                            h('line', {
                                key: 'arm2Tick',
                                attrs: {
                                    ...arm2Tick,
                                    stroke: 'yellow',
                                    'stroke-width': 3
                                }
                            })
                        );
                    }
                }

                if (data.text && vertexValid && arm1Valid && liveArm2) {
                    const dx1t = data.arm1Position[0] - data.vertexPosition[0];
                    const dy1t = data.arm1Position[1] - data.vertexPosition[1];
                    const dx2t = liveArm2[0] - data.vertexPosition[0];
                    const dy2t = liveArm2[1] - data.vertexPosition[1];
                    const tLen1 = Math.hypot(dx1t, dy1t);
                    const tLen2 = Math.hypot(dx2t, dy2t);
                    const arcR = (tLen1 > 0 && tLen2 > 0) ? computeArcRadius(tLen1, tLen2) : 30;
                    let textX = data.vertexPosition[0];
                    let textY = data.vertexPosition[1] - (arcR + 35);
                    if (tLen1 > 0 && tLen2 > 0) {
                        const bx = dx1t / tLen1 + dx2t / tLen2;
                        const by = dy1t / tLen1 + dy2t / tLen2;
                        const bLen = Math.hypot(bx, by);
                        if (bLen > 0.01) {
                            textX = data.vertexPosition[0] + (bx / bLen) * (arcR + 35);
                            textY = data.vertexPosition[1] + (by / bLen) * (arcR + 35);
                        }
                    }
                    nodes.push(
                        h('text', {
                            key: 'angleText',
                            attrs: {
                                x: textX,
                                y: textY,
                                fill: 'yellow',
                                'font-size': 15,
                                'text-anchor': 'middle',
                                'dominant-baseline': 'middle'
                            },
                        }, data.text)
                    );
                }

                return nodes;
            },
        })
    );
}

function mkAngleWidget(renderer, addedMeasurementCallback) {
    const widget = vtkAngle2DWidgetFactory.newInstance(addedMeasurementCallback);
    const camera = renderer.getActiveCamera();
    widget.getManipulator().setUserOrigin(camera.getFocalPoint());
    return widget;
}

const removeWidget = (angleId, angleWidgets, widgetManager, removedMeasurementCallback) => {
    const widgetInfo = angleWidgets.get(angleId);
    if (!widgetInfo) { return; }

    log(`Removing angle widget ${angleId}`);
    widgetManager.removeWidget(widgetInfo.widget);
    angleWidgets.delete(angleId);
    removedMeasurementCallback(angleId);
    log(`Widgets in map: ${[...angleWidgets.keys()]}`);
    log(`Widgets in manager: ${widgetManager.getWidgets().length}`);
};

function addRemoveOnClick(angleId, angleWidgets, widgetManager, removedMeasurementCallback, getRemovingMeasurement) {
    const widgetInfo = angleWidgets.get(angleId);
    if (!widgetInfo || widgetInfo.handle.isDeleted()) { return; }

    const removeOnClick = () => {
        if (!getRemovingMeasurement()) { return; }
        removeWidget(angleId, angleWidgets, widgetManager, removedMeasurementCallback);
    };

    widgetInfo.handle.onEndInteractionEvent(removeOnClick);
}

function removeWidgets(widgetManager, angleWidgets, removedMeasurementCallback) {
    for (const [angleId, _] of angleWidgets) {
        removeWidget(angleId, angleWidgets, widgetManager, removedMeasurementCallback);
    }
}

export function useAngleWidget(parentContext, { measurementIds, angleMeasurementState, addedMeasurementCallback, removedMeasurementCallback }, slicePlaneIndex, activeWidget, slicingMode) {
    const context = useRef(null);
    const parentContextRef = useRef(parentContext);
    const angleMeasurementStateRef = useRef(null);

    useEffect(() => {
        if (parentContext) {

            if (!context.current) {
                const angleWidgets = new Map();
                context.current = { angleWidgets };
            } else if (parentContext.renderer && parentContext.mapper && getDUName(slicingMode) === SlicingModeName.AXIAL) {
                const { widgetManager, mapper, renderer } = parentContext;
                const { angleWidgets } = context.current;
                debug(() => global.debugAngleWidgetManager = widgetManager);
                const currentSlice = mapper.getSlice();
                log(`Current slice: ${currentSlice} vs ${slicePlaneIndex}`);

                if (measurementIds && measurementIds.length > 0) {
                    measurementIds.forEach((angleId) => {
                        if (getDUName(activeWidget) === WidgetType.ANGLE_WIDGET
                            && !angleWidgets.has(angleId)
                            && getDUName(angleMeasurementState) === AngleMeasurementStateName.ADDING) {
                            log(`Adding angle widget ${angleId} at slice ${slicePlaneIndex}`);

                            const angleWidget = mkAngleWidget(renderer, addedMeasurementCallback);
                            const behaviourHandle = widgetManager.addWidget(angleWidget);

                            widgetManager.grabFocus(angleWidget);

                            debug(() => global.debugAngleWidget = angleWidget);
                            debug(() => global.debugAngleHandle = behaviourHandle);

                            const { cleanupCallbacks, updateVNode } =
                                setupSVG(behaviourHandle, renderer, parentContext.genericRenderWindow);

                            angleWidgets.set(
                                angleId,
                                {
                                    sliceIndex: slicePlaneIndex,
                                    handle: behaviourHandle,
                                    widget: angleWidget,
                                    svgCleanupCallback: cleanupCallbacks,
                                    updateVNode: updateVNode
                                });

                            behaviourHandle.setHandleVisibility(false);

                            behaviourHandle.onInteractionEvent(() => {
                                if (behaviourHandle.getPlacedHandlesCount() < 2) return;
                                const angle = angleWidgets.get(angleId).widget.getAngle();

                                if (angle >= 0) {
                                    behaviourHandle.setText(`${angle.toFixed(1)}°`);
                                }
                            });
                        } else if (angleWidgets.has(angleId)) {
                            const widgetInfo = angleWidgets.get(angleId);

                            angleMeasurementStateRef.current = getDUName(angleMeasurementState);
                            const getRemovingMeasurement = () => angleMeasurementStateRef.current === AngleMeasurementStateName.REMOVING;
                            addRemoveOnClick(angleId, angleWidgets, widgetManager, removedMeasurementCallback, getRemovingMeasurement);

                            debug(() => global.debugAngleInfo = widgetInfo);
                            if (!widgetInfo.handle.isDeleted() && !isNullOrUndefined(widgetInfo.sliceIndex) && widgetInfo.sliceIndex.toFixed(0) !== slicePlaneIndex.toFixed(0)) {
                                log(`Angle widget on wrong slice, turn off visibility ${widgetInfo.sliceIndex} vs ${slicePlaneIndex}`);
                                if (!widgetInfo.handle.isDeleted()) {
                                    widgetInfo.handle.setPickable(false);
                                }
                                widgetInfo.updateVNode();
                            } else {
                                log(`Angle widget ${angleId} on correct slice, turn on visibility ${widgetInfo.sliceIndex} vs ${slicePlaneIndex}`);
                                if (!widgetInfo.handle.isDeleted()) {
                                    widgetInfo.handle.setPickable(true);
                                    widgetInfo.updateVNode();
                                }
                            }
                        } else {
                            removedMeasurementCallback(angleId);
                        }
                        widgetManager.enablePicking();
                    });

                    for (const widgetId of [...angleWidgets.keys()]) {
                        if (!measurementIds.includes(widgetId)) {
                            removeWidget(widgetId, angleWidgets, widgetManager, removedMeasurementCallback);
                        }
                    }
                } else if (angleWidgets.size > 0) {
                    log(`No angle ids provided, clearing angle widgets`);
                    removeWidgets(widgetManager, angleWidgets, removedMeasurementCallback);
                    widgetManager.disablePicking();
                }
            }
        }
    }, [parentContext, measurementIds, angleMeasurementState, slicePlaneIndex, activeWidget, slicingMode, addedMeasurementCallback, removedMeasurementCallback]);

    useEffect(() => {
        parentContextRef.current = parentContext;
    }, [parentContext]);

    useEffect(() => {
        log('Mounting angle widget hook');
        return () => {
            if (parentContextRef.current && context.current) {
                log(`Disposing angle widgets with parentContext`);
                const { widgetManager } = parentContextRef.current;
                const { angleWidgets } = context.current;
                if (widgetManager && !widgetManager.isDeleted()) {
                    removeWidgets(widgetManager, angleWidgets);
                }
                angleWidgets.clear();
                context.current = { angleWidgets };
            } else if (context.current) {
                log(`Disposing angle widgets without parentContext`);
                const { angleWidgets } = context.current;
                angleWidgets.forEach((widget) => {
                    widget.svgCleanupCallback();
                });
                angleWidgets.clear();
                context.current = { angleWidgets };
            }
        };
    }, []);
}
